/**
 * Paints a `CardLayout` onto a canvas and returns a PNG.
 *
 * Plain 2D canvas rather than an SVG-to-image round trip: nothing external is
 * drawn, so the canvas is never tainted and `toBlob` just works — which is
 * what the Web Share API and the clipboard need.
 */

import type { CardLayout } from './cardLayout';
import { CARD } from './cardLayout';

/** Mirrors src/styles/theme.css; read live where the page exposes them. */
const PALETTE = {
  bg: ['--color-bg', '#0b0e0c'],
  surface: ['--color-surface', '#121712'],
  surface2: ['--color-surface-2', '#172017'],
  line: ['--color-line', '#1e2a20'],
  pip: ['--color-pip', '#35e07a'],
  pipDim: ['--color-pip-dim', '#1f8a4c'],
  text: ['--color-text', '#d6e5da'],
  textDim: ['--color-text-dim', '#8ba393'],
  textFaint: ['--color-text-faint', '#5d7266'],
} as const;

export const FONT_BODY = `system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif`;
export const FONT_MONO = `"Cascadia Mono", Consolas, "DejaVu Sans Mono", ui-monospace, "Liberation Mono", monospace`;

/** Discord shows large images at ~half size, so 2× keeps the 13px names crisp. */
const SCALE = 2;
const MISSING_ALPHA = 0.28;

export type Colors = Record<keyof typeof PALETTE, string>;

export function colors(): Colors {
  const root = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const out = {} as Colors;
  for (const [key, [cssVar, fallback]] of Object.entries(PALETTE)) {
    out[key as keyof Colors] = root?.getPropertyValue(cssVar).trim() || fallback;
  }
  return out;
}

export function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(`${text.slice(0, mid).trimEnd()}…`).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, lo).trimEnd()}…`;
}

export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function renderCard(layout: CardLayout): Promise<Blob> {
  const c = colors();

  const canvas = document.createElement('canvas');
  canvas.width = layout.width * SCALE;
  canvas.height = layout.height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is not available');
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'middle';

  // Ground and a hairline frame, so the card reads as a card on any chat background.
  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, layout.width, layout.height);
  ctx.strokeStyle = c.line;
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, layout.width - 2, layout.height - 2);

  // ---- header ----
  const right = layout.width - CARD.pad;
  ctx.fillStyle = c.pip;
  ctx.font = `bold 13px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.fillText('🐾 MeowTracker', CARD.pad, 30);

  ctx.fillStyle = c.text;
  ctx.font = `bold 24px ${FONT_BODY}`;
  ctx.fillText(ellipsize(ctx, layout.title, layout.width * 0.62), CARD.pad, 58);

  ctx.fillStyle = c.textDim;
  ctx.font = `13px ${FONT_BODY}`;
  ctx.fillText(ellipsize(ctx, layout.subtitle, layout.width * 0.62), CARD.pad, 82);

  const percent = layout.total === 0 ? 0 : Math.round((layout.done / layout.total) * 100);
  ctx.textAlign = 'right';
  ctx.fillStyle = c.pip;
  ctx.font = `bold 30px ${FONT_MONO}`;
  ctx.fillText(`${layout.done} / ${layout.total}`, right, 54);
  ctx.fillStyle = c.textDim;
  ctx.font = `14px ${FONT_MONO}`;
  ctx.fillText(`${percent}%`, right, 82);
  ctx.textAlign = 'left';

  const barY = CARD.headerHeight - 16;
  roundedRect(ctx, CARD.pad, barY, layout.width - CARD.pad * 2, 6, 3);
  ctx.fillStyle = c.line;
  ctx.fill();
  if (layout.done > 0) {
    const w = ((layout.width - CARD.pad * 2) * layout.done) / layout.total;
    roundedRect(ctx, CARD.pad, barY, Math.max(w, 6), 6, 3);
    ctx.fillStyle = layout.done === layout.total ? c.pip : c.pipDim;
    ctx.fill();
  }

  // ---- groups ----
  const nameX = 18; // the ✓ column
  const nameMax = layout.columnWidth - nameX - 12;

  for (const block of layout.blocks) {
    const titleY = block.y + CARD.groupTitleHeight / 2;
    ctx.fillStyle = c.text;
    ctx.font = `600 14px ${FONT_BODY}`;
    ctx.fillText(ellipsize(ctx, block.title, layout.width - CARD.pad * 2 - 90), CARD.pad, titleY);

    ctx.textAlign = 'right';
    ctx.fillStyle = block.done === block.total ? c.pip : c.textDim;
    ctx.font = `12px ${FONT_MONO}`;
    ctx.fillText(`${block.done} / ${block.total}`, right, titleY);
    ctx.textAlign = 'left';

    ctx.strokeStyle = c.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(CARD.pad, block.y + CARD.groupTitleHeight - 3);
    ctx.lineTo(right, block.y + CARD.groupTitleHeight - 3);
    ctx.stroke();

    for (const cell of block.cells) {
      const { item } = cell;
      const midY = cell.y + CARD.rowHeight / 2;

      ctx.save();
      if (!item.done) ctx.globalAlpha = MISSING_ALPHA;

      if (item.done) {
        ctx.fillStyle = c.pip;
        ctx.font = `bold 13px ${FONT_MONO}`;
        ctx.fillText('✓', cell.x, midY);
      }

      ctx.fillStyle = item.done ? c.text : c.textDim;
      ctx.font = `13px ${FONT_BODY}`;
      ctx.fillText(ellipsize(ctx, item.name, nameMax), cell.x + nameX, midY);
      ctx.restore();
    }
  }

  if (layout.empty) {
    ctx.fillStyle = c.textFaint;
    ctx.font = `14px ${FONT_BODY}`;
    ctx.textAlign = 'center';
    ctx.fillText(layout.empty, layout.width / 2, CARD.headerHeight + CARD.emptyHeight / 2);
    ctx.textAlign = 'left';
  }

  // ---- footer ----
  ctx.fillStyle = c.textFaint;
  ctx.font = `11px ${FONT_BODY}`;
  ctx.fillText(layout.footer, CARD.pad, layout.height - CARD.footerHeight / 2);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob returned null'));
    }, 'image/png');
  });
}
